import { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Camera, Sparkles, X, Loader2, CheckCircle2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';

interface UploadDriverLicenseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  driverId: string;
  driverName: string;
}

interface ExtractedLicense {
  license_number: string | null;
  license_expiry: string | null;
  license_categories: string[] | null;
  first_name: string | null;
  last_name: string | null;
  birth_date: string | null;
}

export const UploadDriverLicenseDialog = ({
  open,
  onOpenChange,
  driverId,
  driverName,
}: UploadDriverLicenseDialogProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [frontImage, setFrontImage] = useState<string | null>(null);
  const [backImage, setBackImage] = useState<string | null>(null);
  const [isExtracting, setIsExtracting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [extracted, setExtracted] = useState<ExtractedLicense | null>(null);
  const frontRef = useRef<HTMLInputElement>(null);
  const backRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setFrontImage(null);
    setBackImage(null);
    setExtracted(null);
    if (frontRef.current) frontRef.current.value = '';
    if (backRef.current) backRef.current.value = '';
  };

  const analyze = async (file: File, side: 'front' | 'back') => {
    setIsExtracting(true);
    try {
      const reader = new FileReader();
      const base64Image: string = await new Promise((resolve, reject) => {
        reader.onload = () => resolve((reader.result as string).split(',')[1]);
        reader.onerror = () => reject(new Error('read failed'));
        reader.readAsDataURL(file);
      });

      const { data, error } = await supabase.functions.invoke('extract-driver-license', {
        body: { imageBase64: base64Image },
      });

      if (error) throw error;

      if (data?.success === false) {
        if (data.error === 'NOT_A_DRIVER_LICENSE') {
          toast({
            title: t('fleet.license.notALicense'),
            description: t('fleet.license.notALicenseDesc'),
            variant: 'destructive',
          });
          if (side === 'front') {
            setFrontImage(null);
            if (frontRef.current) frontRef.current.value = '';
          } else {
            setBackImage(null);
            if (backRef.current) backRef.current.value = '';
          }
          return;
        }
        throw new Error(data.error || 'Extraction failed');
      }

      // Merge extracted data
      setExtracted((prev) => ({
        license_number: data.data.license_number || prev?.license_number || null,
        license_expiry: data.data.license_expiry || prev?.license_expiry || null,
        license_categories: data.data.license_categories || prev?.license_categories || null,
        first_name: data.data.first_name || prev?.first_name || null,
        last_name: data.data.last_name || prev?.last_name || null,
        birth_date: data.data.birth_date || prev?.birth_date || null,
      }));

      toast({
        title: t('fleet.license.extracted'),
        description: side === 'front' ? t('fleet.license.frontDone') : t('fleet.license.backDone'),
      });
    } catch (e) {
      console.error(e);
      toast({
        title: t('fleet.extraction.error'),
        description: e instanceof Error ? e.message : t('fleet.extraction.error'),
        variant: 'destructive',
      });
    } finally {
      setIsExtracting(false);
    }
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>, side: 'front' | 'back') => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      const url = reader.result as string;
      if (side === 'front') setFrontImage(url);
      else setBackImage(url);
    };
    reader.readAsDataURL(file);
    await analyze(file, side);
  };

  const handleSave = async () => {
    if (!extracted?.license_number || !extracted?.license_expiry) {
      toast({
        title: t('fleet.license.incomplete'),
        description: t('fleet.license.incompleteDesc'),
        variant: 'destructive',
      });
      return;
    }
    setIsSaving(true);
    try {
      const { data, error } = await supabase.functions.invoke('save-driver-license-data', {
        body: { driverId, data: extracted },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast({
        title: t('fleet.license.saved'),
        description: t('fleet.license.savedDesc'),
      });
      queryClient.invalidateQueries({ queryKey: ['drivers'] });
      reset();
      onOpenChange(false);
    } catch (e) {
      console.error(e);
      toast({
        title: t('common.error'),
        description: e instanceof Error ? e.message : 'Save failed',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold text-[#37699E] font-['Lexend_Deca']">
            {t('fleet.license.title')} — {driverName}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="p-4 bg-[#0880AE]/10 border border-[#0880AE]/20 rounded-lg">
            <p className="text-sm text-[#2D3748]">
              <strong>{t('fleet.license.howTo')}</strong> {t('fleet.license.howToDesc')}
            </p>
          </div>

          {/* Front */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-[#5A6B7A]">
                {t('fleet.license.front')}
              </span>
              {frontImage && !isExtracting && (
                <Badge className="bg-[#14A38B]">✓ {t('fleet.license.frontDone')}</Badge>
              )}
            </div>
            <input
              ref={frontRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={(e) => handleFile(e, 'front')}
              className="hidden"
            />
            {!frontImage ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => frontRef.current?.click()}
                disabled={isExtracting}
                className="w-full h-auto min-h-[120px] flex-col gap-2 border-2 border-[#0880AE] hover:bg-[#EBF4F8]"
              >
                <Camera className="h-8 w-8 text-[#0880AE]" />
                <span className="text-sm font-medium">{t('fleet.license.captureFront')}</span>
              </Button>
            ) : (
              <div className="relative rounded-lg border-2 border-[#0880AE] overflow-hidden">
                <img src={frontImage} alt="License front" className="w-full h-48 object-cover" />
                {isExtracting && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                    <div className="flex flex-col items-center gap-2 text-white">
                      <Sparkles className="h-8 w-8 animate-pulse" />
                      <p className="text-sm font-medium">{t('fleet.extraction.analyzing')}</p>
                    </div>
                  </div>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setFrontImage(null);
                    if (frontRef.current) frontRef.current.value = '';
                  }}
                  className="absolute top-2 right-2 bg-white/90 hover:bg-white text-[#FF7171] rounded-full"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>

          {/* Back */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-[#5A6B7A]">
                {t('fleet.license.back')}
              </span>
              {backImage && !isExtracting && (
                <Badge className="bg-[#14A38B]">✓ {t('fleet.license.backDone')}</Badge>
              )}
            </div>
            <input
              ref={backRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={(e) => handleFile(e, 'back')}
              className="hidden"
            />
            {!backImage ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => backRef.current?.click()}
                disabled={isExtracting}
                className="w-full h-auto min-h-[120px] flex-col gap-2 border-2 border-[#87ADD4] hover:bg-[#EBF4F8]"
              >
                <Camera className="h-8 w-8 text-[#87ADD4]" />
                <span className="text-sm font-medium">{t('fleet.license.captureBack')}</span>
              </Button>
            ) : (
              <div className="relative rounded-lg border-2 border-[#87ADD4] overflow-hidden">
                <img src={backImage} alt="License back" className="w-full h-48 object-cover" />
                {isExtracting && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                    <div className="flex flex-col items-center gap-2 text-white">
                      <Sparkles className="h-8 w-8 animate-pulse" />
                      <p className="text-sm font-medium">{t('fleet.extraction.analyzing')}</p>
                    </div>
                  </div>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setBackImage(null);
                    if (backRef.current) backRef.current.value = '';
                  }}
                  className="absolute top-2 right-2 bg-white/90 hover:bg-white text-[#FF7171] rounded-full"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>

          {/* Extracted preview */}
          {extracted && (
            <div className="p-4 bg-[#EBF4F8] rounded-lg space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-[#37699E]">
                <CheckCircle2 size={16} className="text-[#14A38B]" />
                {t('fleet.license.extractedData')}
              </div>
              <p className="text-xs text-[#5A6B7A] mb-2 italic">
                {t('fleet.license.lockedNotice')}
              </p>
              <dl className="text-sm space-y-1">
                <div className="flex justify-between">
                  <dt className="text-[#5A6B7A]">{t('fleet.dialogs.licenseNumber')}:</dt>
                  <dd className="font-medium text-[#2D3748]">{extracted.license_number || '—'}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-[#5A6B7A]">{t('fleet.dialogs.licenseExpiry')}:</dt>
                  <dd className="font-medium text-[#2D3748]">{extracted.license_expiry?.slice(0, 10) || '—'}</dd>
                </div>
                {extracted.license_categories && (
                  <div className="flex justify-between">
                    <dt className="text-[#5A6B7A]">{t('fleet.license.categories')}:</dt>
                    <dd className="font-medium text-[#2D3748]">{extracted.license_categories.join(', ')}</dd>
                  </div>
                )}
              </dl>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSaving}
              className="border-[#DBE2EA]"
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleSave}
              disabled={!extracted?.license_number || !extracted?.license_expiry || isSaving || isExtracting}
              className="bg-[#FFBF07] hover:bg-[#E5AB06] text-[#2D3748] font-semibold"
            >
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('fleet.license.save')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
